import { useEffect, useState } from 'react'
import { plateBanner } from '../../assets/index.js'
import { useAnimation } from '../../hooks/useAnimation.js'
import { useAudio } from '../../hooks/useAudio.js'
import { CELL_SIZE } from '../Grid/Cell.js'
import type { ShipType } from '@fleetduel/shared'

interface ShipSunkFXProps {
  /** Pixel position of the primary target cell */
  x: number
  y: number
  shipType: ShipType
  onDone: () => void
}

const SHIP_NAMES: Record<ShipType, string> = {
  destroyer: 'DESTROYER SUNK',
  patrol_boat: 'PATROL BOAT SUNK',
  ghost_sub: 'GHOST SUB SUNK',
}

type Phase = 'reveal' | 'announce' | 'sink' | 'done'

/**
 * Ship Sunk animation: reveal + announcement banner + sink tilt.
 * ART_GUIDE_v2 §2.5. Total ~1.5s (after hit animation).
 */
export function ShipSunkFX({ x, y, shipType, onDone }: ShipSunkFXProps) {
  const { phaseTimer, reduceMotion } = useAnimation()
  const { play } = useAudio()
  const [phase, setPhase] = useState<Phase>('reveal')

  useEffect(() => {
    if (reduceMotion) {
      play('ship-sunk', 'combat')
      onDone()
      return
    }

    // Reveal (400ms) → Announce (500ms) → Sink (600ms)
    // Play ship-sunk sound at reveal start (per AUDIO.md §2: after hit sound finishes)
    play('ship-sunk', 'combat')
    phaseTimer(400, () => {
      setPhase('announce')
      phaseTimer(500, () => {
        setPhase('sink')
        phaseTimer(600, () => {
          setPhase('done')
          onDone()
        })
      })
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (phase === 'done') return null

  return (
    <>
      {/* Announcement banner */}
      {(phase === 'announce' || phase === 'sink') && (
        <div style={{
          position: 'absolute',
          left: x - 80,
          top: y - 56,
          width: 224,
          height: 48,
          zIndex: 20,
          animation: 'banner-in 300ms cubic-bezier(0.22,1,0.36,1) forwards',
          opacity: phase === 'sink' ? undefined : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <img
            src={plateBanner}
            alt=""
            draggable={false}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
            }}
          />
          <span style={{
            position: 'relative',
            zIndex: 1,
            fontSize: 16,
            fontWeight: 700,
            color: '#F4B942',
            letterSpacing: 2,
          }}>
            {SHIP_NAMES[shipType]}
          </span>
        </div>
      )}

      {/* Golden outline pulse on the target cell area */}
      {phase === 'reveal' && (
        <div style={{
          position: 'absolute',
          left: x - 2,
          top: y - 2,
          width: CELL_SIZE + 4,
          height: CELL_SIZE + 4,
          border: '2px solid #F4B942',
          pointerEvents: 'none',
          zIndex: 15,
          animation: 'ship-reveal-pulse 400ms ease-out forwards',
        }} />
      )}
    </>
  )
}
